import { Permission } from '@Constants/Permission.g';
import { useHasPermission } from '@Contexts/appContext';
import { useIsPortalSession } from '@Contexts/session';
import { useFeatureFlags } from '@Contexts/featureFlags';
import { type ApiOrderSnapshot, getEnumContainerOrderActionLabel } from '@Model';
import { Badge, FloatingCard, iconSizeSm, ListRow, Tooltip, useTranslate } from '@synqo/frontend-component-library';
import { IconInfoCircle } from '@tabler/icons-react';
import { useMemo } from 'react';
import { useContainerTypeDetails } from '../Orders/Editing/Containers/useContainerTypeDetails';
import classes from './PortalRequisitionContainersCard.module.css';
import { collectPortalRequisitionContainers, type PortalContainerEntry } from './portalRequisitionContainers';

export interface PortalRequisitionContainersCardProps {
    /** The order snapshots of the requisition; the containers are read off them. */
    snapshots: readonly ApiOrderSnapshot[] | undefined;
}

/**
 * The containers of a requisition: the ones already standing at the site first, then the ones still
 * to come. Each line counts the containers of one type going one way.
 *
 * Renders nothing when there is nothing to say — no container planned, a tenant that does not run the
 * container service at all, or a session that may not read the container types the lines are built from.
 */
// @ai-generated 2026-09-18T11:59:15+00:00 by malte.ulfers@tngtech.com using Claude Opus 5 (1M context)
export function PortalRequisitionContainersCard({ snapshots }: PortalRequisitionContainersCardProps) {
    const i18n = useTranslate('');
    const { isFeatureEnabled } = useFeatureFlags();
    const hasPermission = useHasPermission();
    const isPortalSession = useIsPortalSession();

    // The snapshot carries the containers regardless of the flag, so a tenant without the container
    // service is filtered out here rather than left to show an empty card.
    //
    // The actions come with the snapshot, but describing them takes the container types, which are
    // master data behind their own permission. Without it the card stays away entirely rather than
    // asking and being refused: a session that may not read the types cannot tell an individually
    // managed one from a pooled one, and every container would be listed on a line of its own.
    const canShowContainers =
        isFeatureEnabled('CONTAINER_SERVICE') && hasPermission(Permission.StammdatenContainerTypeRead);

    // The snapshot links its type but does not describe it: how the type is managed and what it is
    // called in the portal are master data, so that a completed order is not re-read according to
    // the master data of the day it was written.
    const containerTypeIds = useMemo(
        () =>
            canShowContainers
                ? (snapshots ?? []).flatMap((snapshot) =>
                      (snapshot.container_actions ?? []).map((action) => action.container_type.foreign_key),
                  )
                : [],
        [canShowContainers, snapshots],
    );
    const containerTypes = useContainerTypeDetails(containerTypeIds);

    const containers = useMemo(
        () =>
            canShowContainers
                ? collectPortalRequisitionContainers(snapshots, containerTypes, isPortalSession)
                : { booked: [], planned: [] },
        [canShowContainers, snapshots, containerTypes, isPortalSession],
    );

    if (containers.booked.length === 0 && containers.planned.length === 0) {
        return undefined;
    }

    return (
        <FloatingCard title={i18n('Container')} collapsible={false}>
            <ContainerSection entries={containers.booked} />
            <ContainerSection label={i18n('Geplant')} entries={containers.planned} />
        </FloatingCard>
    );
}

interface ContainerSectionProps {
    /** Heading above the lines. Left out for the stage the card is named after. */
    label?: string;
    entries: readonly PortalContainerEntry[];
}

/** The containers of one stage. Renders nothing when that stage is empty. */
// @ai-generated 2026-09-15T06:44:44+00:00 by malte.ulfers@tngtech.com using Claude Opus 5 (1M context)
function ContainerSection({ label, entries }: ContainerSectionProps) {
    const i18n = useTranslate('');

    if (entries.length === 0) {
        return undefined;
    }

    return (
        <>
            {label && <p className={classes.sectionLabel}>{label}</p>}
            {entries.map((entry) => (
                <ListRow
                    key={entry.id}
                    className={classes.row}
                    title={entry.kind === 'individual' ? entry.label : `${entry.quantity} × ${entry.label}`}
                    titleSuffix={<EntryDetails entry={entry} />}
                    rightSection={
                        <Badge variant="accent">{i18n(getEnumContainerOrderActionLabel(entry.action))}</Badge>
                    }
                />
            ))}
        </>
    );
}

/**
 * What follows the type on a line: which kind of container it is, its number where it has one, and
 * an icon carrying what the planner noted.
 *
 * Every part brings its own separator, because the suffix sits flush against the title.
 */
// @ai-generated 2026-09-15T06:44:44+00:00 by malte.ulfers@tngtech.com using Claude Opus 5 (1M context)
function EntryDetails({ entry }: { entry: PortalContainerEntry }) {
    const i18n = useTranslate('');

    const isIndividual = entry.kind === 'individual';
    const remarks = isIndividual ? [entry.remarks].filter(Boolean) : entry.remarks;
    const number = isIndividual ? entry.containerNumber : '';

    return (
        <>
            <span className={classes.kind}>
                {' · '}
                {i18n(isIndividual ? 'Einzelbehälter' : 'Sammelbehälter')}
            </span>

            {number && (
                <span className={classes.numbers}>
                    {' · '}
                    {i18n('Nr.')} {number}
                </span>
            )}

            {remarks.length > 0 && (
                <Tooltip label={remarks.join(' · ')}>
                    <span className={classes.remarkIcon} aria-label={i18n('Bemerkung')}>
                        <IconInfoCircle size={iconSizeSm} />
                    </span>
                </Tooltip>
            )}
        </>
    );
}
